"use client";

import { Fragment, type ComponentProps, type ReactNode } from "react";
import { isAdminHref, useOpenableHref } from "@/hooks/use-openable-href";
import type { Row, RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { RecordGridRow } from "./record-grid-row";
import type { RowMenuController } from "./use-row-menu";
import type { RecordObjectDef, TableAction } from "../types";

const LOADING_ROW_COUNT = 8;

const LOADING_BAR_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-2/5"];

function LoadingRows({
  label,
  columnSpan,
}: {
  label: string;
  columnSpan: number;
}) {
  return (
    <tbody role="status">
      <tr className="sr-only">
        <td colSpan={columnSpan}>Loading {label.toLowerCase()}</td>
      </tr>
      {Array.from({ length: LOADING_ROW_COUNT }, (_, rowIndex) => (
        <tr key={rowIndex} aria-hidden="true">
          {Array.from({ length: columnSpan }, (_, cellIndex) => (
            <td
              key={cellIndex}
              className="h-[var(--table-row-height)] border-b border-border px-[var(--table-cell-px)] py-[var(--table-cell-py)] align-middle"
            >
              <Skeleton
                className={cn(
                  "h-3",
                  LOADING_BAR_WIDTHS[
                    (rowIndex + cellIndex) % LOADING_BAR_WIDTHS.length
                  ],
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

interface RowRenderProps<TData extends RowData> {
  object: RecordObjectDef<TData>;
  columnWidths: Map<string, number>;
  columnOrderKey: string;
  hasFiller: boolean;
  rowActions?: TableAction<TData>[];
  onRowClick?: (row: TData) => void;
  onRowHover?: (id: string | null) => void;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  activeRowId?: string | null;
  getRowClassName?: (row: TData) => string | undefined;
  renderExpandedRow?: (row: TData) => ReactNode;
}

interface RecordGridBodyProps<
  TData extends RowData,
> extends RowRenderProps<TData> {
  rows: Row<RecordGridFeatures, TData>[];
  columnSpan: number;
  isLoading: boolean;
  isStale: boolean;
}

export function RecordGridEmpty({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="sticky left-0 hidden w-full flex-1 flex-col items-center justify-center gap-1 px-6 py-12 text-center md:flex">
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? (
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function RecordGridBody<TData extends RowData>({
  rows,
  columnSpan,
  isLoading,
  isStale,
  ...rowProps
}: RecordGridBodyProps<TData>) {
  if (rows.length === 0 && isLoading) {
    return (
      <LoadingRows label={rowProps.object.label} columnSpan={columnSpan} />
    );
  }

  if (rows.length === 0) return null;

  return (
    <tbody inert={isStale} className={cn(isStale && "opacity-60")}>
      {rows.map((row) => (
        <RecordGridRowGroup key={row.id} row={row} {...rowProps} />
      ))}
    </tbody>
  );
}

function GatedRecordGridRow<TData extends RowData>({
  href,
  ...rest
}: ComponentProps<typeof RecordGridRow<TData>>) {
  const openable = useOpenableHref(href);
  return <RecordGridRow {...rest} href={openable} />;
}

function RecordGridRowGroup<TData extends RowData>({
  row,
  object,
  renderExpandedRow,
  getRowClassName,
  activeRowId,
  ...rest
}: RowRenderProps<TData> & { row: Row<RecordGridFeatures, TData> }) {
  const expandedContent = renderExpandedRow?.(row.original);
  const rowHref = object.getRowHref?.(row.original);
  const Row =
    rowHref !== undefined && isAdminHref(rowHref)
      ? GatedRecordGridRow
      : RecordGridRow;
  return (
    <Fragment>
      <Row
        row={row}
        href={rowHref}
        isActive={activeRowId === row.id}
        isSelected={row.getIsSelected()}
        isExpanded={renderExpandedRow ? Boolean(expandedContent) : undefined}
        className={getRowClassName?.(row.original)}
        {...rest}
      />
      {expandedContent ? (
        <tr className="border-b border-border bg-muted/30">
          <td colSpan={row.getVisibleCells().length}>{expandedContent}</td>
        </tr>
      ) : null}
    </Fragment>
  );
}
